import { speciesList } from '../region';
import { fetchMedia } from './pack';

// Image counterpart to audio.ts. Photos live in the same Cache Storage bucket as
// the recordings, so they come back as a Response and have to be turned into an
// object URL before an <img> can show them.
//
// Unlike audio there is no synthetic fallback: a made-up bird photo would be
// actively misleading, so a missing image resolves to null and the caller shows
// a placeholder instead.

const urlCache = new Map<string, Promise<string | null>>();

/** Resolve a pack-relative image path to a displayable URL, or null. */
export function imageUrl(path: string): Promise<string | null> {
  const hit = urlCache.get(path);
  if (hit) return hit;
  const p = imageUrlUncached(path);
  urlCache.set(path, p);
  return p;
}

async function imageUrlUncached(path: string): Promise<string | null> {
  if (!path) return null;
  const res = await fetchMedia(path);
  if (!res) return null;
  try {
    // Object URLs are never revoked: a region is a few dozen images and they are
    // reused across the library list and every species page, so holding them is
    // cheaper than re-decoding. Same trade-off as audio.ts's buffer cache.
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

/**
 * Whether photo questions can be asked at all — i.e. whether the region's media
 * pack has been downloaded.
 *
 * Probed off the first species with a photo rather than assumed from the
 * manifest: the manifest lists the photos a region HAS, and until the pack is
 * downloaded none of them resolve. Every screen that deals a photo question
 * asks this first, because the alternative is a round of placeholders.
 */
export async function photosReady(): Promise<boolean> {
  const file = speciesList()
    .map((s) => s.photos[0]?.file)
    .find(Boolean);
  return !!file && !!(await imageUrl(file));
}
